import {useEffect, useState} from 'react';
import {Content, Overlay, Portal, Root, Title, Trigger} from '@radix-ui/react-dialog';
import cx from 'classnames';
import NextLink from 'next/link';
import {useRouter} from 'next/router';

import {Button, Icon, TallyLogo, Text} from '#component/base';
import * as selectCss from '#component/base/Select/Select.css';
import {useThrottle, useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {InternalNavBarProps} from './InternalNavBar.types';

import * as css from './InternalNavBar.css';

export const InternalNavBar = ({
  className,
  links = [],
  showLogo = false,
  ...otherProps
}: InternalNavBarProps) => {
  const [activeValue, setActiveValue] = useState(links[0]?.value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const {back} = useRouter();
  const {isDesktop} = useViewportSize();
  const activeLabel = links.find(({value}) => value === activeValue)?.label;
  const threshold = isDesktop ? 0.7 : 0.3;

  const handleScroll = () => {
    window.scrollY > 0 ? setScrolled(true) : setScrolled(false);
  };
  const throttledHandleScroll = useThrottle(handleScroll, 100, {leading: false, trailing: true});

  const handleAnchorClick = (nextValue: string) => {
    const targetSection = document.getElementById(nextValue);

    if (targetSection) {
      setActiveValue(nextValue);
      targetSection.scrollIntoView({behavior: 'smooth'});
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;

        if (entry.isIntersecting) {
          setActiveValue(entry.target.id);
        }
      },
      {root: null, rootMargin: '0px', threshold},
    );

    links.forEach(({value}) => {
      const targetSection = document.getElementById(value);

      if (targetSection) {
        observer.observe(targetSection);
      }
    });

    return () => {
      links.forEach(({value}) => {
        const targetSection = document.getElementById(value);

        if (targetSection) {
          observer.unobserve(targetSection);
        }
      });
    };
  }, [links, threshold]);

  return (
    <header className={cx(css.root({scrolled}), className)} {...otherProps}>
      <nav className={sprinkles({layout: 'grid', w: 'full'})}>
        <div
          className={sprinkles({
            colStart: 1,
            colSpan: [3, 7, 11],
            flex: 'flex',
            items: 'center',
            gap: 32,
          })}>
          {showLogo && (
            <NextLink href="/" passHref>
              <a aria-label="Tally Health™">
                <TallyLogo size={40} sizeType="height" color="burgundy" />
              </a>
            </NextLink>
          )}

          {!!links?.length && (
            <>
              <div className={sprinkles({flex: ['flex', 'none']})}>
                <Root open={isOpen} onOpenChange={setIsOpen}>
                  <Text as={Trigger} className={css.mobileNavAnchor} variant="buttonSmall">
                    {activeLabel}

                    <Icon
                      aria-label="Jump to section"
                      className={css.dropdownIcon}
                      color="currentColor"
                      name="CaretDown"
                      style={{transform: isOpen ? 'rotate(-180deg)' : 'none'}}
                    />
                  </Text>

                  <Portal>
                    <Overlay />

                    <Content className={selectCss.mobileContent}>
                      <Text
                        as={Title}
                        className={selectCss.label}
                        fontWeight="semiBold"
                        variant="heading4">
                        <span>Jump to Section</span>

                        <Icon aria-hidden color="doveGray" name="CaretDown" weight="bold" />
                      </Text>

                      <ul className={selectCss.mobileList}>
                        {links.map(({label, value}, i) => {
                          const isChecked = value === activeValue;

                          return (
                            <li key={i}>
                              <Text
                                aria-checked={isChecked}
                                as="button"
                                className={sprinkles({flex: 'flex', gap: 16, p: 16, w: 'full'})}
                                color={isChecked ? 'black' : 'doveGray'}
                                onClick={() => {
                                  handleAnchorClick(value);
                                  setIsOpen(false);
                                }}>
                                {label}
                              </Text>
                            </li>
                          );
                        })}
                      </ul>
                    </Content>
                  </Portal>
                </Root>
              </div>

              <ul className={sprinkles({display: ['none', 'flex'], gap: 32})}>
                {links.map(({value, label}) => (
                  <li key={label}>
                    <Text
                      as="a"
                      className={css.navAnchor({isActive: activeValue === value})}
                      color="dustyGray"
                      href={`#${value}`}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        handleAnchorClick(value);
                      }}
                      variant="buttonSmall">
                      {label}
                    </Text>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div
          className={sprinkles({colStart: [4, 8, 12], colSpan: 1, flex: 'flex', content: 'end'})}>
          <Button aria-label="Go back" variant="secondary" icon="X" onClick={back} />
        </div>
      </nav>
    </header>
  );
};
